import { useEffect } from 'react';
import Highlight from '@tiptap/extension-highlight';
import Typography from '@tiptap/extension-typography';
import Link from '@tiptap/extension-link';
import type { AnyExtension } from '@tiptap/core';
import { EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';

import { transformPastedHTML, transformPastedText } from '@arthelper/shared/paste-sanitize';
import '../assets/styles/editor.css';

interface EditorProps {
  content: string;
  isEditing: boolean;
  editCanceled?: boolean;
  onUpdate: (content: string | null) => void;
}

export default ({ content, isEditing, onUpdate, editCanceled = false }: EditorProps) => {
  const extensions: AnyExtension[] = [
    StarterKit as AnyExtension,
    Highlight as AnyExtension,
    Link.configure({ openOnClick: !isEditing }) as AnyExtension,
    Typography as AnyExtension,
  ];

  const editor = useEditor({
    extensions,
    content,
    editable: isEditing,
    editorProps: {
      attributes: {
        class: 'text-lg focus:outline-hidden',
      },
      transformPastedHTML,
      transformPastedText,
    },
    onUpdate: ({ editor }) => {
      if (editor.isEditable) {
        onUpdate(editor.getHTML());
      }
    },
  });

  useEffect(() => {
    editor?.setEditable(isEditing);
  }, [isEditing]);

  useEffect(() => {
    if (editor) {
      editor.commands.setContent(content);
    }
  }, [content]);

  useEffect(() => {
    if (editCanceled) {
      editor?.commands.setContent(content);
    }
  }, [editCanceled]);

  return (
    <div className={`rounded-md w-full relative mt-1 p-2 ${isEditing ? 'ring-2 ring-black' : ''}`}>
      <EditorContent editor={editor} />
    </div>
  );
};
